import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { TreatmentSupportForm } from '@/components/customer/TreatmentSupport';
import { ProgressStrip } from './ProgressStrip';
import { VerifyPanel, type VerifyApi } from './VerifyPanel';
import { ActionStrip } from './ActionStrip';
import { SuccessPanel } from './SuccessPanel';

type PageState =
  | { kind: 'flow'; outerStep: 1 | 2 | 3 | 4 | 5 }
  | { kind: 'success'; caseNumber?: string | number };

export function RequestTreatmentPage() {
  const { dir, t } = useLanguage();
  const { currentUser } = useAuth();
  const [state, setState] = useState<PageState>({ kind: 'flow', outerStep: 1 });
  const [verifyApi, setVerifyApi] = useState<VerifyApi | null>(null);
  const [wizardValid, setWizardValid] = useState(false);
  const [isSubmittingFinal, setIsSubmittingFinal] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  const stateKind = state.kind;
  const flowStep = state.kind === 'flow' ? state.outerStep : null;

  useEffect(() => {
    if (currentUser && stateKind === 'flow' && flowStep === 1) {
      setState({ kind: 'flow', outerStep: 2 });
    }
  }, [currentUser, stateKind, flowStep]);

  const isFlow = state.kind === 'flow';
  const outerStep = isFlow ? state.outerStep : 5;

  let primaryLabel = '';
  let primaryDisabled = false;
  let primaryBusy = false;
  let onPrimary = () => {};
  let backLabel: string | null = null;
  let onBack: (() => void) | null = null;

  if (isFlow) {
    if (outerStep === 1) {
      primaryLabel = verifyApi?.primaryLabel ?? '';
      primaryDisabled = verifyApi?.primaryDisabled ?? true;
      primaryBusy = verifyApi?.primaryBusy ?? false;
      onPrimary = () => verifyApi?.primaryAction();
      backLabel = verifyApi?.backLabel ?? null;
      onBack = verifyApi?.backAction ?? null;
    } else if (outerStep < 5) {
      primaryLabel = t('landing.requestTreatment.next');
      primaryDisabled = !wizardValid;
      onPrimary = () =>
        setState((s) =>
          s.kind === 'flow' && s.outerStep < 5
            ? { kind: 'flow', outerStep: (s.outerStep + 1) as 1 | 2 | 3 | 4 | 5 }
            : s,
        );
      backLabel = t('landing.requestTreatment.back');
      onBack = () =>
        setState((s) =>
          s.kind === 'flow' && s.outerStep > 2
            ? { kind: 'flow', outerStep: (s.outerStep - 1) as 1 | 2 | 3 | 4 | 5 }
            : s,
        );
    } else {
      primaryLabel = t('landing.requestTreatment.submitRequest');
      primaryDisabled = !wizardValid || isSubmittingFinal;
      primaryBusy = isSubmittingFinal;
      onPrimary = () => {
        if (isSubmittingFinal) return;
        setIsSubmittingFinal(true);
        formRef.current?.requestSubmit();
      };
      backLabel = t('landing.requestTreatment.back');
      onBack = () => setState({ kind: 'flow', outerStep: 4 });
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={dir}>
      <main className="flex-grow flex items-center justify-center px-4 py-10">
        <Card className="w-full max-w-3xl overflow-hidden">
          {state.kind === 'flow' ? (
            <>
              <ProgressStrip currentStep={outerStep} totalSteps={5} />
              <div className="px-6 py-6 min-h-[240px]">
                {outerStep === 1 ? (
                  <VerifyPanel
                    onReady={setVerifyApi}
                    onVerified={() => setState({ kind: 'flow', outerStep: 2 })}
                  />
                ) : (
                  <TreatmentSupportForm
                    hideHeader
                    hideFooterNav
                    externalStep={(outerStep - 1) as 1 | 2 | 3 | 4}
                    onValidityChange={setWizardValid}
                    formRef={formRef}
                    onSubmitted={(caseNumber) => {
                      setIsSubmittingFinal(false);
                      setState({ kind: 'success', caseNumber });
                    }}
                  />
                )}
              </div>
              <ActionStrip
                primaryLabel={primaryLabel}
                primaryDisabled={primaryDisabled}
                primaryBusy={primaryBusy}
                onPrimary={onPrimary}
                backLabel={backLabel}
                onBack={onBack}
              />
            </>
          ) : (
            <SuccessPanel caseNumber={state.caseNumber} />
          )}
        </Card>
      </main>
    </div>
  );
}

export default RequestTreatmentPage;
